import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getStorageSafe } from './firebaseConfig';

export const StorageService = {
  /**
   * Uploads a voice reply blob to Firebase Storage.
   * Path: voiceReplies/{userId}/{timestamp}.webm
   */
  uploadChatImage: async (userId: string, file: File): Promise<string> => {
    try {
      const storage = await getStorageSafe();
      const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const path = `users/${userId}/chatImages/${Date.now()}.${ext}`;
      const storageRef = ref(storage, path);
      const snapshot = await uploadBytes(storageRef, file, { contentType: file.type || 'image/jpeg' });
      return await getDownloadURL(snapshot.ref);
    } catch (error: any) {
      throw new Error(`Image upload failed: ${error.message}`);
    }
  },

  /**
   * Uploads a yap video file to Firebase Storage.
   * Path: users/{userId}/yapVideos/{timestamp}.{ext}
   */
  uploadYapVideo: async (userId: string, file: File): Promise<string> => {
    try {
      const storage = await getStorageSafe();
      const ext = (file.name.split('.').pop() || 'mp4').toLowerCase();
      const path = `users/${userId}/yapVideos/${Date.now()}.${ext}`;
      const storageRef = ref(storage, path);
      const snapshot = await uploadBytes(storageRef, file, {
        contentType: file.type || 'video/mp4',
      });
      return await getDownloadURL(snapshot.ref);
    } catch (error: any) {
      console.error('[StorageService] Yap video upload failed:', error);
      throw new Error(`Video upload failed: ${error.message || 'Unknown error'} (${error.code || 'no-code'})`);
    }
  },

  /**
   * Uploads an image attached to a yap reply.
   * Path: users/{userId}/replyImages/{timestamp}.{ext}
   */
  uploadReplyImage: async (userId: string, file: File): Promise<string> => {
    try {
      const storage = await getStorageSafe();
      const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const path = `users/${userId}/replyImages/${Date.now()}.${ext}`;
      const storageRef = ref(storage, path);
      const snapshot = await uploadBytes(storageRef, file, { contentType: file.type || 'image/jpeg' });
      return await getDownloadURL(snapshot.ref);
    } catch (error: any) {
      throw new Error(`Image upload failed: ${error.message}`);
    }
  },

  uploadVoiceReply: async (userId: string, blob: Blob): Promise<string> => {
    try {
      const storage = await getStorageSafe();
      const timestamp = Date.now();
      // Use the actual extension from the blob if possible, default to m4a for compatibility
      const extension = blob.type.includes('mp4') ? 'm4a' : (blob.type.includes('webm') ? 'webm' : 'm4a');
      const fileName = `${timestamp}.${extension}`;
      
      const storageRef = ref(storage, `users/${userId}/voiceReplies/${fileName}`);

      // 📤 UPLOAD
      console.log(`[StorageService] Uploading voice reply for ${userId} (${blob.type})...`);
      const snapshot = await uploadBytes(storageRef, blob, {
        contentType: blob.type || 'audio/mpeg',
      });

      // 🔗 GET URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log(`[StorageService] Voice reply uploaded: ${downloadURL}`);
      return downloadURL;
    } catch (error: any) {
      console.error('[StorageService] Voice reply upload failed:', error);
      // Log more details to console for the developer
      const errorMessage = error.message || 'Unknown error';
      const errorCode = error.code || 'no-code';
      throw new Error(`Upload failed: ${errorMessage} (${errorCode}). Please check your permissions.`);
    }
  }
};
